import { useEffect, useMemo, useState } from 'react';
import type { Editor } from '@tiptap/react';
import Icon from 'components/editor/GridDndEditor/TiptapEditor/components/Icon';
import IcoArrowDropDownRounded from 'components/icon/icons/IcoArrowDropDownRounded';
import PyMuiMenu from 'components/editor/GridDndEditor/TiptapEditor/components/generalComponents/PyMuiMenu';
import PyMuiMenuItem from 'components/editor/GridDndEditor/TiptapEditor/components/generalComponents/PyMuiMenuItem';
import PyMuiMenuLabel from 'components/editor/GridDndEditor/TiptapEditor/components/generalComponents/PyMuiMenuLabel';
import PyMuiButton from 'components/editor/GridDndEditor/TiptapEditor/components/generalComponents/PyMuiButton';
import { getFontFamilyOptions, toCssFontFamily } from '../utils';
import { DEFAULT_FONT_FAMILY } from 'components/editor/grid/reduxStore/defaultTextDefaultValue';
import Stack from '@mui/material/Stack';

const normalizeFontFamily = (value?: string) => value?.trim().replace(/^['"]|['"]$/g, '') ?? '';

type Props = {
  editor: Editor | null;
  disabled?: boolean;
};

const FontFamilyButton = ({ editor, disabled = false }: Props) => {
  const options = useMemo(() => getFontFamilyOptions(), []);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedValue, setSelectedValue] = useState<string | undefined>(undefined);
  const normalizedSelectedValue = useMemo(() => normalizeFontFamily(selectedValue), [selectedValue]);

  const selectedOption = useMemo(() => {
    const normalizedSelected = normalizedSelectedValue.toLowerCase();
    return options.find((opt) => normalizeFontFamily(opt.value).toLowerCase() === normalizedSelected);
  }, [options, normalizedSelectedValue]);
  const selectedLabel = selectedOption?.name || normalizedSelectedValue;
  const selectedFontForStyle = selectedOption?.value || selectedLabel;

  useEffect(() => {
    if (!editor) return;
    const syncSelectedValue = () => {
      const value = editor.getAttributes('textStyle')?.fontFamily || DEFAULT_FONT_FAMILY;
      setSelectedValue(value);
    };

    syncSelectedValue();
    editor.on('selectionUpdate', syncSelectedValue);
    editor.on('transaction', syncSelectedValue);

    return () => {
      editor.off('selectionUpdate', syncSelectedValue);
      editor.off('transaction', syncSelectedValue);
    };
  }, [editor]);

  const handleOpenMenu: React.MouseEventHandler<HTMLElement> = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (value: string) => {
    editor?.chain().focus().setFontFamily(value).run();
    setSelectedValue(value);
    handleClose();
  };

  return (
    <Stack direction="row" alignItems="center">
      <PyMuiButton
        width={152}
        testId="font-family-button"
        isMenuOpen={Boolean(anchorEl)}
        disabled={disabled}
        onClickAction={handleOpenMenu}
      >
        <PyMuiMenuLabel data-testid="font-family-label" style={{ fontFamily: toCssFontFamily(selectedFontForStyle) }}>
          {selectedLabel}
        </PyMuiMenuLabel>
        <Icon>
          <IcoArrowDropDownRounded />
        </Icon>
      </PyMuiButton>

      <PyMuiMenu
        testId="font-family-menu"
        id="select-font-family-menu"
        menuListProps="select-font-family-button"
        anchorEl={anchorEl}
        isMenuOpen={Boolean(anchorEl)}
        handleClose={handleClose}
      >
        {options.map(({ name, value }) => (
          <PyMuiMenuItem
            key={value}
            testId={`font-family-menu-item-${name.replace(/\s+/g, '-').toLowerCase()}`}
            onClick={() => handleSelect(value)}
            isActive={normalizeFontFamily(value).toLowerCase() === normalizedSelectedValue.toLowerCase()}
            menuItem={name}
            isFontFamily
            customStyle={{ minWidth: 220 }}
          />
        ))}
      </PyMuiMenu>
    </Stack>
  );
};

export default FontFamilyButton;
